import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";

const AUTO_CLOSE_MS = 2500;

interface TopDrawerNotificationProps {
  message: string;
  visible: boolean;
  onClose: () => void;
}

export default function TopDrawerNotification({
  message,
  visible,
  onClose,
}: TopDrawerNotificationProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasVisibleRef = useRef(false);
  const [isClosing, setIsClosing] = useState(false);
  const [playEntryAnimation, setPlayEntryAnimation] = useState(false);

  useLayoutEffect(() => {
    if (visible) {
      setPlayEntryAnimation(false);
      const id = setTimeout(() => setPlayEntryAnimation(true), 0);
      return () => clearTimeout(id);
    } else {
      setPlayEntryAnimation(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onClose();
    }, AUTO_CLOSE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [visible, onClose]);

  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
      setIsClosing(false);
    } else if (wasVisibleRef.current) {
      wasVisibleRef.current = false;
      setIsClosing(true);
    }
  }, [visible]);

  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsClosing(false);
    }
  };

  if (!visible && !isClosing) return null;

  return (
    <div
      className={classNames(
        styles.drawer,
        visible && playEntryAnimation && styles.drawerVisible,
        isClosing && styles.drawerClosing,
      )}
      role="alert"
      onAnimationEnd={handleAnimationEnd}
    >
      <span className={styles.message}>{message}</span>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close notification"
      >
        <Icons.Cross className={styles.closeIcon} />
      </button>
    </div>
  );
}
